import { useState } from "react";
import { Button } from "../ui/Button";
import { CrisisResources } from "./CrisisResources";
import { t } from "../../i18n/strings";
import { useSpeech } from "../../voice/useSpeech";
import type { SafetyState } from "../../engine/types";

/**
 * §11: the questionnaire stops; a fixed banner appears above the chat; TTS
 * is SUPPRESSED for the safety message by default — replaced by an
 * explicit "Read aloud" button, because a household smart speaker reading
 * a disclosure aloud unprompted is a privacy risk. No resume, ever: the
 * only controls are ending the interview or starting a brand-new one.
 *
 * "safety.banner.text" (below) is UI chrome Fieldnote owns — a fixed English
 * and Spanish pair looked up in strings.ts — and is NOT the same string as
 * `safety.message` on the wire, which is the Rails-authored, human-written
 * fixed safety message (§21 non-negotiable #5: never generated, translated
 * at runtime, or made dynamic). This component renders `safety.message`
 * as-is when present and falls back to nothing when it is not.
 */
export function SafetyBanner({
  safety,
  locale,
  onEnd,
  onRestart,
}: {
  safety: SafetyState;
  locale: "en" | "es";
  onEnd: () => void;
  onRestart: () => void;
}) {
  const [spoken, setSpoken] = useState(false);
  // §21 item 12: never present an unbuilt feature as working. Phase 1 has
  // no CHW roster, assignment or messaging, so "hand off" cannot notify
  // anyone. The button says so plainly instead of silently doing nothing
  // (it used to be wired to a no-op). The pause itself, the follow-up flag
  // and the export are real; only the notification is not built.
  const [handoffNoteShown, setHandoffNoteShown] = useState(false);
  // Shared with MicButton/Composer's voice input (§16). This is the ONLY
  // place that ever calls `speak()` for the safety message — the hook is a
  // plain imperative API with no auto-speak of its own, so nothing is
  // spoken until this explicit button handler runs it.
  const { speak, canSpeak } = useSpeech();

  function readAloud() {
    if (!canSpeak || !safety.message) return;
    speak(safety.message, locale);
    setSpoken(true);
  }

  return (
    <div className="space-y-3 border-b border-border bg-clay-dim p-4">
      <p className="font-medium text-ink">{t(locale, "safety.banner.text")}</p>
      <div className="flex items-center gap-2">
        <Button variant="secondary" onClick={readAloud} aria-pressed={spoken}>
          {t(locale, "safety.readAloud.button")}
        </Button>
        <span className="text-xs text-ink-soft">{t(locale, "safety.readAloud.caption")}</span>
      </div>
      <CrisisResources resources={safety.resources ?? []} locale={locale} />
      <div className="flex gap-2">
        <Button
          variant="secondary"
          onClick={() => {
            setHandoffNoteShown(true);
            onEnd();
          }}
        >
          {t(locale, "safety.button.end")}
        </Button>
        <Button variant="ghost" onClick={onRestart}>
          {t(locale, "safety.button.restart")}
        </Button>
      </div>
      {handoffNoteShown && (
        <p role="status" className="text-sm text-ink">
          {t(locale, "safety.handoff.notInPrototype")}
        </p>
      )}
    </div>
  );
}
